import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk, ArrowLeft, Envelope } from "@phosphor-icons/react";

const blank = () => ({
  name: "New template",
  subject: "",
  html_body: "<p>Hi {name},</p>\n<p>...</p>",
  delay_days: 1,
  order: 99,
  active: true,
  is_digest: false,
});

export default function AdminLeadTemplates() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/leads/templates`);
      setItems(r.data.items || []);
      if (!selected && r.data.items?.[0]) setSelected(r.data.items[0]);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!selected) return;
    try {
      if (selected._id) {
        await api.patch(`/admin/leads/templates/${selected._id}`, selected);
      } else {
        const r = await api.post(`/admin/leads/templates`, selected);
        setSelected({ ...selected, _id: r.data.id });
      }
      toast.success("Saved");
      setDirty(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async () => {
    if (!selected?._id || !window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/admin/leads/templates/${selected._id}`);
      toast.success("Deleted");
      setSelected(null);
      await load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const setField = (k, v) => { setSelected({ ...selected, [k]: v }); setDirty(true); };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-templates-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/admin/leads" className="text-xs font-bold uppercase text-zinc-500 hover:text-[#6043EF] flex items-center gap-1"><ArrowLeft size={12}/> Leads</Link>
          <h1 className="text-2xl font-bold tracking-tight text-[#09090B]">Drip Templates</h1>
          <button onClick={() => { setSelected(blank()); setDirty(true); }} data-testid="new-template-btn"
            className="ml-auto inline-flex items-center gap-1 h-9 px-3 bg-[#6043EF] hover:bg-[#4933d1] text-white text-xs font-bold uppercase tracking-wider rounded-lg">
            <Plus size={13} weight="bold"/> New
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          <div className="border-2 border-zinc-200 rounded-lg overflow-hidden">
            <ul className="divide-y divide-zinc-100">
              {items.map((t) => (
                <li key={t._id}>
                  <button onClick={() => { setSelected(t); setDirty(false); }} data-testid={`template-${t._id}`}
                    className={`w-full text-left p-3 hover:bg-zinc-50 ${selected?._id === t._id ? "bg-violet-50 border-l-2 border-[#6043EF]" : ""}`}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-mono text-[10px] font-bold text-zinc-400">#{t.order}</span>
                      <span className="text-[13px] font-bold text-[#09090B] truncate">{t.name}</span>
                      {!t.active && <span className="ml-auto text-[9px] font-bold uppercase text-zinc-400">OFF</span>}
                      {t.is_digest && <span className="ml-auto text-[9px] font-bold uppercase text-violet-600">DIGEST</span>}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">{t.subject || <i>(no subject)</i>}</div>
                    <div className="text-[10px] text-zinc-400 mt-0.5">delay {t.delay_days}d</div>
                  </button>
                </li>
              ))}
              {items.length === 0 && !loading && <li className="p-4 text-center text-zinc-400 text-sm">No templates</li>}
            </ul>
          </div>

          <div className="border-2 border-zinc-200 rounded-lg p-4">
            {!selected ? <p className="text-sm text-zinc-500">Select a template on the left</p> : (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <Envelope size={16} className="text-[#6043EF]" />
                  <input value={selected.name} onChange={(e) => setField("name", e.target.value)} data-testid="tmpl-name"
                    className="flex-1 h-9 px-2 border-2 border-zinc-200 rounded font-bold text-[15px]" />
                  <button onClick={save} disabled={!dirty} data-testid="save-tmpl"
                    className="inline-flex items-center gap-1 h-9 px-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-bold uppercase tracking-wider rounded">
                    <FloppyDisk size={12} weight="fill"/> Save
                  </button>
                  <button onClick={del} disabled={!selected._id} data-testid="delete-tmpl"
                    className="inline-flex items-center gap-1 h-9 px-3 bg-rose-100 hover:bg-rose-200 text-rose-700 text-xs font-bold uppercase tracking-wider rounded disabled:opacity-40">
                    <Trash size={12} weight="bold"/>
                  </button>
                </div>
                <label className="text-[10px] font-bold uppercase text-zinc-500">Subject</label>
                <input value={selected.subject} onChange={(e) => setField("subject", e.target.value)} data-testid="tmpl-subject"
                  className="w-full h-9 px-2 border-2 border-zinc-200 rounded mt-1 mb-3" />
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <div><label className="text-[10px] font-bold uppercase text-zinc-500">Order</label>
                    <input type="number" value={selected.order} onChange={(e) => setField("order", parseInt(e.target.value, 10))} data-testid="tmpl-order"
                      className="w-full h-9 px-2 border-2 border-zinc-200 rounded mt-1"/></div>
                  <div><label className="text-[10px] font-bold uppercase text-zinc-500">Delay (days)</label>
                    <input type="number" step="0.5" value={selected.delay_days} onChange={(e) => setField("delay_days", parseFloat(e.target.value))} data-testid="tmpl-delay"
                      className="w-full h-9 px-2 border-2 border-zinc-200 rounded mt-1"/></div>
                  <label className="flex items-end gap-2 h-9"><input type="checkbox" checked={!!selected.active} onChange={(e) => setField("active", e.target.checked)} data-testid="tmpl-active"/> Active</label>
                  <label className="flex items-end gap-2 h-9"><input type="checkbox" checked={!!selected.is_digest} onChange={(e) => setField("is_digest", e.target.checked)} data-testid="tmpl-isdigest"/> Digest (top-10)</label>
                </div>
                <label className="text-[10px] font-bold uppercase text-zinc-500">HTML body — vars: {"{name}"} {"{rank}"} {"{category}"} {"{domicile}"}; blocks: {"{{colleges_html}}"} {"{{cta_url}}"}</label>
                <textarea value={selected.html_body} onChange={(e) => setField("html_body", e.target.value)} data-testid="tmpl-body"
                  className="w-full h-64 px-3 py-2 border-2 border-zinc-200 rounded mt-1 font-mono text-[12px]" spellCheck={false} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
